import { InputLabel } from '@mui/material';
import type { FC } from 'react';
import type { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  SelectTeamingLocation,
  SelectTeamingReason,
} from 'features/teaming/teaming-base/components';
import type { TeamRequestFormOptions } from 'features/teaming/teaming-base/interfaces';
import styled from 'features/theme/styled';
import theme from 'features/theme/theme';
import { SelectGender, SelectLanguage } from 'features/ui/components';
import type { CustomSelectItem } from 'features/ui/elements';
import { Button } from 'features/ui/elements';

interface TeamRequestBaseFormProps {
  control: Control<TeamRequestFormOptions>;
  isValid: boolean;
  isLoading: boolean;
  reasonOptions: CustomSelectItem[];
  locationOptions: CustomSelectItem[];
  closePopover: () => void;
  onSubmitButtonClick: () => void;
}

export const TeamingBaseRequestForm: FC<TeamRequestBaseFormProps> = ({
  control,
  isValid,
  isLoading,
  reasonOptions,
  closePopover,
  locationOptions,
  onSubmitButtonClick,
}) => {
  const { t: ct } = useTranslation();
  const { t } = useTranslation('translation', {
    keyPrefix: 'teamRequest',
  });

  return (
    <>
      <TopLabel>{t('teamingLocation')}</TopLabel>
      <Controller
        name='location'
        control={control}
        rules={{ required: true }}
        render={({ field: { value, onChange } }) => (
          <SelectTeamingLocation
            options={locationOptions}
            value={value}
            onChange={onChange}
          />
        )}
      />
      <Label>{t('requestReason')}</Label>
      <Controller
        name='reason'
        control={control}
        rules={{ required: true }}
        render={({ field: { value, onChange } }) => (
          <SelectTeamingReason
            options={reasonOptions}
            value={value}
            onChange={onChange}
          />
        )}
      />
      <Label>{ct('genderPreferenceQuestion')}</Label>
      <Controller
        name='gender'
        control={control}
        rules={{ required: true }}
        render={({ field: { value, onChange } }) => (
          <SelectGender value={value} onChange={onChange} />
        )}
      />
      <Label>{t('requestLanguage')}</Label>
      <Controller
        name='language'
        control={control}
        rules={{ required: true }}
        render={({ field: { value, onChange } }) => (
          <SelectLanguage value={value} onChange={onChange} />
        )}
      />
      <SubmitButton
        color='primary'
        size='small'
        fullWidth
        title={t('requestTeam')}
        autoFocus
        loading={isLoading}
        disabled={!isValid}
        onClick={onSubmitButtonClick}
      >
        {t('requestTeam')}
      </SubmitButton>
      <CancelButton
        color='secondary'
        size='small'
        fullWidth
        disabled={isLoading}
        title={ct('cancel')}
        onClick={closePopover}
      >
        {ct('cancel')}
      </CancelButton>
    </>
  );
};

const TopLabel = styled(InputLabel)`
  margin-bottom: ${theme.spacing(3)};
`;

const Label = styled(TopLabel)`
  margin-top: ${theme.spacing(8)};
`;

const SubmitButton = styled(Button)`
  margin-top: ${theme.spacing(8)};
`;

const CancelButton = styled(Button)`
  margin-top: ${theme.spacing(4)};
`;
